import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { categoryMap } from '@/mocks/manga';

const categoryKeys = Object.keys(categoryMap);

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          <Link
            to="/"
            className="flex items-center gap-2 group"
            aria-label="香香漫画首页"
          >
            <div className="w-9 h-9 flex items-center justify-center bg-primary-500 rounded-lg">
              <i className="ri-book-open-line text-white text-lg"></i>
            </div>
            <span className={`font-heading text-xl whitespace-nowrap transition-colors duration-200 ${
              isScrolled ? 'text-foreground-900' : 'text-white'
            }`}>
              香香漫画
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-1" aria-label="主导航">
            <Link
              to="/"
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors duration-200 whitespace-nowrap ${
                location.pathname === '/'
                  ? isScrolled ? 'bg-primary-100 text-primary-700' : 'bg-white/20 text-white'
                  : isScrolled ? 'text-foreground-600 hover:text-primary-500 hover:bg-background-100' : 'text-white/80 hover:text-white hover:bg-white/10'
              }`}
            >
              首页
            </Link>
            {categoryKeys.map((key) => (
              <Link
                key={key}
                to={`/category/${key}`}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors duration-200 whitespace-nowrap ${
                  location.pathname.includes(`/category/${key}`)
                    ? isScrolled ? 'bg-primary-100 text-primary-700' : 'bg-white/20 text-white'
                    : isScrolled ? 'text-foreground-600 hover:text-primary-500 hover:bg-background-100' : 'text-white/80 hover:text-white hover:bg-white/10'
                }`}
              >
                {categoryMap[key]}
              </Link>
            ))}
            <Link
              to="/about"
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors duration-200 whitespace-nowrap ${
                location.pathname === '/about'
                  ? isScrolled ? 'bg-primary-100 text-primary-700' : 'bg-white/20 text-white'
                  : isScrolled ? 'text-foreground-600 hover:text-primary-500 hover:bg-background-100' : 'text-white/80 hover:text-white hover:bg-white/10'
              }`}
            >
              关于我们
            </Link>
          </nav>

          <div className="hidden md:flex items-center gap-2">
            <div className="relative">
              <input
                type="text"
                placeholder="搜索漫画..."
                className={`w-48 pl-9 pr-3 py-2 text-sm rounded-full border transition-all duration-200 focus:outline-none focus:w-64 ${
                  isScrolled
                    ? 'bg-background-100 border-background-200 text-foreground-800 placeholder-foreground-400 focus:border-primary-400'
                    : 'bg-white/15 border-white/20 text-white placeholder-white/50 focus:bg-white/25 focus:border-white/40'
                }`}
              />
              <i className={`ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-sm ${
                isScrolled ? 'text-foreground-400' : 'text-white/60'
              }`}></i>
            </div>
          </div>

          <button
            className="md:hidden w-9 h-9 flex items-center justify-center rounded-lg"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="菜单"
          >
            <i className={`text-xl ${isScrolled ? 'text-foreground-800' : 'text-white'} ${
              isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'
            }`}></i>
          </button>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70 animate-in slide-in-from-top">
          <div className="px-4 py-3 space-y-1">
            <Link
              to="/"
              className={`block px-4 py-2.5 rounded-lg text-sm font-medium ${
                location.pathname === '/' ? 'bg-primary-100 text-primary-700' : 'text-foreground-700 hover:bg-background-100'
              }`}
            >
              首页
            </Link>
            {categoryKeys.map((key) => (
              <Link
                key={key}
                to={`/category/${key}`}
                className={`block px-4 py-2.5 rounded-lg text-sm font-medium ${
                  location.pathname.includes(`/category/${key}`) ? 'bg-primary-100 text-primary-700' : 'text-foreground-700 hover:bg-background-100'
                }`}
              >
                {categoryMap[key]}
              </Link>
            ))}
            <Link
              to="/about"
              className={`block px-4 py-2.5 rounded-lg text-sm font-medium ${
                location.pathname === '/about' ? 'bg-primary-100 text-primary-700' : 'text-foreground-700 hover:bg-background-100'
              }`}
            >
              关于我们
            </Link>
            <div className="pt-2">
              <div className="relative">
                <input
                  type="text"
                  placeholder="搜索漫画..."
                  className="w-full pl-9 pr-3 py-2.5 text-sm rounded-full bg-background-100 border border-background-200 text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-400"
                />
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-sm text-foreground-400"></i>
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}